import React, { useEffect, useState } from "react";
import {
  Button,
  Breadcrumb,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Row,
  Stack,
  Table,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import PubSub from "pubsub-js";
import { ROUTES } from "../../constants/webRouting";
import fuziElevatorsApi from "../../api/fuziElevatorsApi";
import { getUserDetails } from "../../utils/helper";

// ── Constants ─────────────────────────────────────────────────
const ELEVATOR_TYPE_OPTIONS = [
  "Passenger",
  "Passenger cum Goods",
  "Passenger cum Stretcher",
  "Passenger cum Bed",
  "Goods",
].map((v) => ({ label: v, value: v }));
const BUILDING_TYPE_OPTIONS = [
  "Villa",
  "Residential",
  "Commercial",
  "Retail",
  "Hospital",
  "Hotel",
  "Infra",
  "School",
  "College",
  "Temple",
].map((v) => ({ label: v, value: v }));
const MOTOR_OPTIONS = [
  "Vacuum",
  "Geared",
  "Gearless",
  "Hydraulic",
  "Wittur",
].map((v) => ({ label: v, value: v }));
const CABIN_FINISH_OPTIONS = [
  "Mild Steel",
  "Stainless Steel",
  "Golden",
  "Rose Gold",
].map((v) => ({ label: v, value: v }));
const DOOR_VISION_OPTIONS = [
  "Non Vision",
  "Small Vision",
  "Big Vision",
  "Full Vision",
].map((v) => ({ label: v, value: v }));
const DOOR_OPENING_OPTIONS = [
  "Center Opening",
  "Side Opening - Left Hand Side",
  "Side Opening - Right Hand Side",
].map((v) => ({ label: v, value: v }));

const DEFAULT_FLOORS = [
  {
    floor_from: "Basement 3",
    floor_to: "Basement 2",
    height_mm: "",
    sort_order: 1,
  },
  {
    floor_from: "Basement 2",
    floor_to: "Basement 1",
    height_mm: "",
    sort_order: 2,
  },
  {
    floor_from: "Basement 1",
    floor_to: "Basement",
    height_mm: "",
    sort_order: 3,
  },
  {
    floor_from: "Basement",
    floor_to: "Ground Floor",
    height_mm: "",
    sort_order: 4,
  },
  {
    floor_from: "Ground Floor",
    floor_to: "First Floor",
    height_mm: "",
    sort_order: 5,
  },
  {
    floor_from: "First Floor",
    floor_to: "Second Floor",
    height_mm: "",
    sort_order: 6,
  },
  {
    floor_from: "Second Floor",
    floor_to: "Third Floor",
    height_mm: "",
    sort_order: 7,
  },
  {
    floor_from: "Third Floor",
    floor_to: "Fourth Floor",
    height_mm: "",
    sort_order: 8,
  },
  {
    floor_from: "Fourth Floor",
    floor_to: "Fifth Floor",
    height_mm: "",
    sort_order: 9,
  },
  {
    floor_from: "Fifth Floor",
    floor_to: "Sixth Floor",
    height_mm: "",
    sort_order: 10,
  },
  {
    floor_from: "Sixth Floor",
    floor_to: "Seventh Floor",
    height_mm: "",
    sort_order: 11,
  },
  {
    floor_from: "Seventh Floor",
    floor_to: "Eighth Floor",
    height_mm: "",
    sort_order: 12,
  },
  {
    floor_from: "Eighth Floor",
    floor_to: "Ninth Floor",
    height_mm: "",
    sort_order: 13,
  },
];

const toSelectValues = (str) => {
  if (!str || str.trim() === "") return [];
  return str.split(",").map((v) => ({ label: v.trim(), value: v.trim() }));
};

const SiteVisitReportEdit = () => {
  const userInfo = getUserDetails();
  const location = useLocation();
  const navigate = useNavigate();

  const [report, setReport] = useState(
    location.state?.id ? location.state : {},
  );
  const [lineItems, setLineItems] = useState(
    location.state?.line_items?.length > 0
      ? location.state.line_items
      : DEFAULT_FLOORS,
  );
  const [leads, setLeads] = useState([]);
  const [validated, setValidated] = useState(false);
  const [assignUsers, setAssignUsers] = useState([]);

  // Load users
  useEffect(() => {
    async function loadUsers() {
      const users = await fuziElevatorsApi.fetchUsers();
      if (Array.isArray(users)) setAssignUsers(users);
    }
    loadUsers();
  }, []);

  useEffect(() => {
    if (assignUsers.length === 0) return;
    if (!report.owner_ids || report.owner_ids.length === 0) return;
    if (typeof report.owner_ids[0] === "object" && report.owner_ids[0]?.value)
      return;

    const converted = report.owner_ids
      .map((id) => {
        const user = assignUsers.find((u) => u.id === id);
        return user ? { value: user.id, label: user.username } : null;
      })
      .filter(Boolean);

    if (converted.length > 0) {
      setReport((prev) => ({ ...prev, owner_ids: converted }));
    }
  }, [assignUsers]);

  // Load leads
  useEffect(() => {
    async function init() {
      const result = await fuziElevatorsApi.fetchLeads({});
      if (Array.isArray(result)) {
        setLeads(
          result.map((l) => ({
            value: l.id,
            label: `${l.enquiry_no} - ${l.lead_name}`,
            phone: l.phone,
            whatsapp_no: l.whatsapp_no,
            address: l.address,
            customer_name: l.lead_name,
            owner_ids: l.owner_ids || [],
          })),
        );
      }
    }
    init();
  }, []);

  const handleLeadSelect = (selected) => {
    if (!selected) return;
    setReport((prev) => ({
      ...prev,
      lead_id: selected.value,
      enquiry_no: selected.label,
      customer_name: selected.customer_name,
      phone: selected.phone,
      whatsapp_no: selected.whatsapp_no,
      site_address: selected.address,
      // lead.owner_ids already {label,value}[] from lead table
      owner_ids: Array.isArray(selected.owner_ids)
        ? selected.owner_ids.filter((item) => item?.value)
        : [],
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setReport((prev) => ({ ...prev, [name]: value }));
  };

  const handleLineItemChange = (index, field, value) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addFloor = () => {
    if (lineItems.length >= 20) return;
    setLineItems((prev) => [
      ...prev,
      {
        floor_from: "",
        floor_to: "",
        height_mm: "",
        sort_order: prev.length + 1,
      },
    ]);
  };

  const removeFloor = (index) => {
    setLineItems((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((item, i) => ({ ...item, sort_order: i + 1 })),
    );
  };

  const handleCancel = () => {
    if (report.id) {
      navigate(`${ROUTES.SITE_VISIT_REPORT.LIST}/${report.id}`, {
        state: report,
      });
    } else {
      navigate(ROUTES.SITE_VISIT_REPORT.LIST);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!report.lead_id) {
      setValidated(true);
      return PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Validation Error",
        message: "Please select a Lead / Enquiry",
      });
    }

    const filledItems = lineItems.filter(
      (item) => item.floor_from || item.floor_to || item.height_mm,
    );

    const payload = {
      ...report,
      line_items: filledItems,
      owner_ids: Array.isArray(report.owner_ids)
        ? report.owner_ids
            .filter((item) => item?.value)
            .map((item) => item.value)
        : [],
    };

    if (payload.id) {
      const result = await fuziElevatorsApi.saveSiteVisitReport(payload);
      if (result?.success) {
        await fuziElevatorsApi.saveSiteVisitReportFloors(
          payload.id,
          filledItems,
        );
        PubSub.publish("RECORD_SAVED_TOAST", {
          title: "Record Updated",
          message: "Site Visit Report updated successfully",
        });
        navigate(`${ROUTES.SITE_VISIT_REPORT.LIST}/${payload.id}`, {
          state: payload,
        });
      } else {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Error",
          message: result?.errors || "Some error found",
        });
      }
    } else {
      const result = await fuziElevatorsApi.createSiteVisitReport(payload);
      if (result?.success || result?.id) {
        PubSub.publish("RECORD_SAVED_TOAST", {
          title: "Record Created",
          message: "Site Visit Report created successfully",
        });
        navigate(
          `${ROUTES.SITE_VISIT_REPORT.LIST}/${result?.data?.id || result?.id}`,
          { state: result?.data || result },
        );
      } else {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Error",
          message: result?.errors || "Some error found",
        });
      }
    }
  };

  return (
    <Container className="pb-5">
      <Row>
        <Col md={12} className="sticky-breadcrumb">
          <Breadcrumb>
            <Breadcrumb.Item onClick={() => navigate(ROUTES.HOME)}>
              Home
            </Breadcrumb.Item>
            <Breadcrumb.Item
              onClick={() => navigate(ROUTES.SITE_VISIT_REPORT.LIST)}
            >
              Site Visit Reports
            </Breadcrumb.Item>
          </Breadcrumb>
        </Col>

        <Col md={12}>
          <Form onSubmit={handleSubmit} noValidate validated={validated}>
            <Card className="shadow">
              <CardHeader className="bg-custom text-white sticky-card-header">
                <Row className="w-100">
                  <Col lg={6}>
                    <div className="d-flex align-items-center h-100">
                      <h5 className="m-0">
                        {report.id
                          ? "Edit Site Visit Report"
                          : "Create Site Visit Report"}
                      </h5>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <Stack direction="horizontal" className="float-end" gap={2}>
                      <Button size="sm" className="btn-save" type="submit">
                        Save
                      </Button>
                      <Button
                        size="sm"
                        className="btn-cancel"
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                <Row className="view-form">
                  {/* ── Basic Info ─────────────────────── */}
                  <Col md={12} className="section-header">
                    Basic Information
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Enquiry / Lead
                      </Form.Label>
                      <Select
                        placeholder="Select Enquiry"
                        options={leads}
                        value={
                          report.lead_id
                            ? leads.find((l) => l.value === report.lead_id)
                            : null
                        }
                        onChange={handleLeadSelect}
                      />
                      {validated && !report.lead_id && (
                        <div className="feedback-invalid">
                          Enquiry is required
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Elevator Type
                      </Form.Label>
                      <Select
                        placeholder="Select Elevator Type"
                        options={ELEVATOR_TYPE_OPTIONS}
                        value={
                          report.elevator_type
                            ? {
                                label: report.elevator_type,
                                value: report.elevator_type,
                              }
                            : null
                        }
                        onChange={(e) =>
                          setReport((p) => ({ ...p, elevator_type: e.value }))
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Customer Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="customer_name"
                        placeholder="Enter Customer Name"
                        value={report.customer_name || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Site Address
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="site_address"
                        placeholder="Enter Site Address"
                        value={report.site_address || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Mobile No.
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="phone"
                        placeholder="Enter Mobile No."
                        value={report.phone || ""}
                        onChange={handleChange}
                        maxLength={10}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Site Person Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="site_person_name"
                        placeholder="Enter site person name"
                        value={report.site_person_name || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Site Person Mobile
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="site_person_mobile"
                        placeholder="Enter site person mobile"
                        value={report.site_person_mobile || ""}
                        onChange={handleChange}
                        maxLength={10}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Assigned Users
                      </Form.Label>
                      <Select
                        isMulti
                        placeholder="Select Assigned Users"
                        options={assignUsers.map((user) => ({
                          value: user.id,
                          label: user.username,
                        }))}
                        value={
                          Array.isArray(report.owner_ids)
                            ? report.owner_ids.filter((item) => item?.value)
                            : []
                        }
                        onChange={(selected) =>
                          setReport((prev) => ({
                            ...prev,
                            owner_ids: selected || [],
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>

                  {/* ── Technical Info ──────────────────── */}
                  <Col md={12} className="section-header">
                    Technical Information
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Pit Available (mm)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="lift_pit"
                        placeholder="Enter pit size in mm"
                        value={report.lift_pit || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Machine Room Available
                      </Form.Label>
                      <Form.Select
                        name="machine_room"
                        value={report.machine_room || ""}
                        onChange={handleChange}
                      >
                        <option value="">---Select---</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Number of Openings
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="number_of_openings"
                        placeholder="Enter number of openings"
                        value={report.number_of_openings || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Number of Stops
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="total_stops"
                        placeholder="Enter number of stops"
                        value={report.total_stops || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Building Type
                      </Form.Label>
                      <Select
                        placeholder="Select building type"
                        options={BUILDING_TYPE_OPTIONS}
                        value={
                          report.segment
                            ? { label: report.segment, value: report.segment }
                            : null
                        }
                        onChange={(e) =>
                          setReport((p) => ({ ...p, segment: e.value }))
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Motor Type
                      </Form.Label>
                      <Select
                        isMulti
                        placeholder="Select motor type"
                        options={MOTOR_OPTIONS}
                        value={toSelectValues(report.motor)}
                        onChange={(selected) =>
                          setReport((p) => ({
                            ...p,
                            motor: selected.map((s) => s.value).join(","),
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>

                  {/* ── Door & Cabin ────────────────────── */}
                  <Col md={12} className="section-header">
                    Door & Cabin Information
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Cabin Finish
                      </Form.Label>
                      <Select
                        isMulti
                        placeholder="Select cabin finish"
                        options={CABIN_FINISH_OPTIONS}
                        value={toSelectValues(report.cabin_metal)}
                        onChange={(selected) =>
                          setReport((p) => ({
                            ...p,
                            cabin_metal: selected.map((s) => s.value).join(","),
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Door Vision
                      </Form.Label>
                      <Select
                        isMulti
                        placeholder="Select door vision"
                        options={DOOR_VISION_OPTIONS}
                        value={toSelectValues(report.door_vision)}
                        onChange={(selected) =>
                          setReport((p) => ({
                            ...p,
                            door_vision: selected.map((s) => s.value).join(","),
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Door Opening
                      </Form.Label>
                      <Select
                        placeholder="Select door opening"
                        options={DOOR_OPENING_OPTIONS}
                        value={
                          report.door_opening
                            ? {
                                label: report.door_opening,
                                value: report.door_opening,
                              }
                            : null
                        }
                        onChange={(e) =>
                          setReport((p) => ({ ...p, door_opening: e.value }))
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Number of Door Panels
                      </Form.Label>
                      <Form.Select
                        name="door_panels"
                        value={report.door_panels || "2"} 
                        onChange={handleChange}
                      >
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                        <option value="5">5</option>
                        <option value="6">6</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Door Width (mm)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="door_width"
                        placeholder="Enter door width in mm"
                        value={report.door_width || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Door Height (mm)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="door_height"
                        placeholder="Enter door height in mm"
                        value={report.door_height || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  {/* ── Car & Capacity ───────────────────── */}
                  <Col md={12} className="section-header">
                    Car Size & Capacity
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Car Width (mm)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="car_internal_width"
                        placeholder="Enter car width in mm"
                        value={report.car_internal_width || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Car Depth (mm)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="car_internal_depth"
                        placeholder="Enter car depth in mm"
                        value={report.car_internal_depth || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Capacity (Persons)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="passenger_capacity"
                        placeholder="Enter person capacity"
                        value={report.passenger_capacity || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Capacity (Kg)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="elevator_capacity"
                        placeholder="Enter kg capacity"
                        value={report.elevator_capacity || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  {/* ── Lift Well ────────────────────────── */}
                  <Col md={12} className="section-header">
                    Lift Well Size
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Lift Well Width (mm)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="shaft_width"
                        placeholder="Enter shaft width in mm"
                        value={report.shaft_width || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Lift Well Depth (mm)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="shaft_debth"
                        placeholder="Enter shaft depth in mm"
                        value={report.shaft_debth || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Plaster
                      </Form.Label>
                      <Form.Select
                        name="shaft_construction"
                        value={report.shaft_construction || ""}
                        onChange={handleChange}
                      >
                        <option value="">---Select---</option>
                        <option value="with plaster">With Plaster</option>
                        <option value="without plaster">Without Plaster</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {/* ── Visit Info ───────────────────────── */}
                  <Col md={12} className="section-header">
                    Visit Information
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Site Visited By
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="engineer_name"
                        placeholder="Enter engineer name"
                        value={report.engineer_name || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Site Visit Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="site_visit_date"
                        value={report.site_visit_date || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={12}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Remark
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="remark"
                        placeholder="Enter remark"
                        value={report.remark || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  {/* ── Floor Heights ────────────────────── */}
                  <Col md={12} className="section-header">
                    Floor to Floor Heights
                  </Col>

                  <Col lg={12}>
                    <Table bordered hover responsive size="sm">
                      <thead className="bg-custom text-white">
                        <tr>
                          <th>#</th>
                          <th>Floor From</th>
                          <th>Floor To</th>
                          <th>Height (mm)</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((item, index) => (
                          <tr key={index}>
                            <td className="text-center align-middle">
                              {index + 1}
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="text"
                                placeholder="e.g. Ground Floor"
                                value={item.floor_from || ""}
                                onChange={(e) =>
                                  handleLineItemChange(
                                    index,
                                    "floor_from",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="text"
                                placeholder="e.g. First Floor"
                                value={item.floor_to || ""}
                                onChange={(e) =>
                                  handleLineItemChange(
                                    index,
                                    "floor_to",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="number"
                                placeholder="mm"
                                value={item.height_mm || ""}
                                onChange={(e) =>
                                  handleLineItemChange(
                                    index,
                                    "height_mm",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                            <td className="text-center">
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => removeFloor(index)}
                              >
                                <i className="fa fa-trash-o" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {lineItems.length < 20 && (
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={addFloor}
                      >
                        <i className="fa fa-plus" /> Add Floor (Max 20)
                      </Button>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default SiteVisitReportEdit;